import { createContext, type ReactNode, type RefObject, useContext } from 'react';

import type { DropdownSize } from './Dropdown.types';

/**
 * Window-space rect of the trigger, measured on press by the native
 * `<DropdownTrigger>` so `<DropdownContent>` can anchor its panel to it. Same
 * shape as `PopoverAnchorRect`; kept local so cross-platform code carries no
 * RN type surface.
 */
export interface DropdownAnchorRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Context shared by the `Dropdown*` compound parts. Provided by the platform
 * `<Dropdown>` root (Radix Popover on web, bottom sheet on native).
 */
export interface DropdownContextValue {
  /** Whether the menu is currently open. */
  open: boolean;
  /** Trigger press — toggles the popover (web) / presents the sheet (native). */
  toggle: () => void;
  /** Closes the menu (item select, programmatic dismiss). */
  close: () => void;
  /** Row band inherited by the content panel and every item. */
  size: DropdownSize;
  /** Test ID prefix from the root — content derives `{testID}-popup`. */
  testID?: string;
  /**
   * Web only — the ref'd trigger wrapper Radix anchors to. `DropdownContent`'s
   * outside-dismiss guard checks whether a dismissal originated inside it.
   */
  anchorRef?: RefObject<HTMLSpanElement | null>;
  /**
   * Native only — the trigger's window-space rect, measured on press. The
   * panel renders inside a transparent `Modal`, which shares no layout with
   * the trigger's tree, so a measured rect is the only way to anchor to it.
   */
  anchorRect?: DropdownAnchorRect | null;
  /** Native only — how `<DropdownTrigger>` reports the rect it measured. */
  setAnchorRect?: (rect: DropdownAnchorRect) => void;
}

export const DropdownContext = createContext<DropdownContextValue | null>(null);

/** Read the dropdown context; throws when used outside a `<Dropdown>`. */
export function useDropdownContext(component: string): DropdownContextValue {
  const ctx = useContext(DropdownContext);
  if (!ctx) throw new Error(`<${component}> must be rendered inside a <Dropdown>.`);
  return ctx;
}

/**
 * Native only — what a `<DropdownLayer>` offers the menus inside it: a place to
 * render their panel in the SAME window instead of a `Modal`. See
 * `DropdownLayer.native`.
 */
export interface DropdownLayerValue {
  /** Show (or refresh) this menu's panel; `close` is what a tap outside it calls. */
  show: (id: string, panel: ReactNode, close: () => void) => void;
  /** Take this menu's panel down (no-op when another menu's panel is up). */
  hide: (id: string) => void;
}

export const DropdownLayerContext = createContext<DropdownLayerValue | null>(null);

/**
 * Close the enclosing menu from anywhere inside a `<DropdownContent>`.
 *
 * `DropdownItem` closes itself, so this is only for interactive content the
 * items don't own — a trailing action button in a row's `rightSlot` that
 * stops propagation, a custom panel body. Throws outside a `<Dropdown>`.
 */
export function useDropdownClose(): () => void {
  return useDropdownContext('useDropdownClose').close;
}

/**
 * Context shared by a `<DropdownSub>`'s trigger + content. Web fills every
 * field (hover open/close with a grace delay); native only really uses
 * `open` + `toggle` (inline expand) and provides no-op timers.
 */
export interface DropdownSubContextValue {
  /** Whether the submenu is currently open / expanded. */
  open: boolean;
  /** Press toggle (touch / keyboard on web, the only opener on native). */
  toggle: () => void;
  /** Open now — hover-in on the web trigger. */
  openNow: () => void;
  /**
   * Schedule a close after a short grace delay — hover-out on the web trigger
   * or flyout. The delay lets the pointer travel from the trigger row into
   * the flyout without the submenu collapsing mid-way.
   */
  scheduleClose: () => void;
  /** Cancel a scheduled close — pointer re-entered the trigger or flyout. */
  cancelClose: () => void;
}

export const DropdownSubContext = createContext<DropdownSubContextValue | null>(null);

/** Read the submenu context; throws when used outside a `<DropdownSub>`. */
export function useDropdownSubContext(component: string): DropdownSubContextValue {
  const ctx = useContext(DropdownSubContext);
  if (!ctx) throw new Error(`<${component}> must be rendered inside a <DropdownSub>.`);
  return ctx;
}
